import { useCallback } from 'react';
import { pm2SitemapStore, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { useLocation, useNavigationManager } from '@provider/navigation';
import { useTrackingManager } from '@provider/tracking';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { Select, IconButton } from '@ui/kit';
import { CourseIcon, RoundedCheckmarkIcon, PlusIcon } from '@ui/kit/icons';
import { useMatchBreakpoint } from '@utils/react';
import { useActiveRevision } from './hook.active-revision.js';
import { selectTooltip, header } from './molecule.revision-select.style.js';
import { useCourseRevisionState } from './store.js';
import { getFirstValidChapterDescriptorInCourse } from './util.get-first-valid-chapter-descriptor-in-course.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useActivateRevision } from './interactions/activate.js';
import { useStartRevision } from './interactions/start.js';
import { useGetAllRevisionsQuery } from './api/query.get-all.js';

const defaultCourseId = "__default";
function RevisionSelect(props) {
  const tracker = useTrackingManager();
  const location = useLocation();
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const user = useUserIdentityState(state => state.user);
  const revisions = useGetAllRevisionsQuery(props.courseDescriptor.id);
  const activeRevision = useActiveRevision(props.courseDescriptor.id);
  const navigation = useNavigationManager();
  const currentDescriptor = findPageDescriptorByPath(sitemap, location.pathname);
  const activateRevision = useActivateRevision(props.courseDescriptor.id);
  const isEditing = useCourseRevisionState(state => state.isEditing);
  const editableRevision = useCourseRevisionState(state => state.editableRevision);
  const options = revisions.data?.map(it => ({
    id: it.id,
    label: it.name
  })) ?? [];
  const onChange = useCallback(id => {
    if (id === activeRevision?.id) {
      return;
    }
    if (id === defaultCourseId) {
      activateRevision("default");
    } else {
      tracker.trackEvent("userarea-delinger.revisions.activated");
      const revision = revisions.data?.find(it => it.id === id);
      if (!revision) {
        return;
      }
      const firstValidChapter = getFirstValidChapterDescriptorInCourse(revision, props.courseDescriptor);

      // In case we're currently on a page that is considered disabled in the
      // new revision, then auto-navigate to the first available chapter.
      if (currentDescriptor && revision.disabledContentIds[currentDescriptor.id]?.disabled && firstValidChapter) {
        // ... and ensure that the navigation has been fully completed before
        // applying the revision to avoid flashes of content before navigation
        // ends.
        const unsubscribe = navigation.subscribe(() => {
          const location = navigation.getLocation();
          if (location.pathname === firstValidChapter.path) {
            activateRevision(revision);
            unsubscribe();
          }
        });
        navigation.replace(firstValidChapter.path);
      } else {
        activateRevision(revision);
      }
    }
  }, [activateRevision, activeRevision?.id, currentDescriptor, navigation, props.courseDescriptor, revisions.data, tracker]);
  const selectedValue = isEditing && (!activeRevision && !editableRevision || editableRevision?.id !== activeRevision?.id) ? ["Opret ny version"] : [activeRevision?.id ?? defaultCourseId];
  const tooltip = (() => {
    if (options.length > 0 && !isEditing) {
      return {
        body: /*#__PURE__*/jsxs(Fragment, {
          children: ["Du har flere versioner af forl\xF8bet tilg\xE6ngelig.", /*#__PURE__*/jsx("br", {}), /*#__PURE__*/jsx("br", {}), "V\xE6lg den version, du skal bruge."]
        }),
        ariaType: "description",
        placement: "right-start",
        className: selectTooltip
      };
    }
    if (isEditing) {
      return {
        body: "Det er ikke muligt at ændre version mens du redigerer.",
        ariaType: "description",
        placement: "right-start",
        className: selectTooltip
      };
    }
    return undefined;
  })();
  return /*#__PURE__*/jsx(Select, {
    className: props.className,
    theme: "compact",
    listboxTheme: "default",
    label: "Versioner",
    collapseOnClick: true,
    dropdownOffsetPx: 6,
    toggleValue: onChange,
    toggledIcon: /*#__PURE__*/jsx(RoundedCheckmarkIcon, {}),
    selectedValues: selectedValue,
    disabled: isEditing,
    options: [...options, {
      id: defaultCourseId,
      label: "Originalt forløb",
      defaultIcon: /*#__PURE__*/jsx(CourseIcon, {})
    }],
    header: !isMobile && user?.role === "teacher" && !!revisions.data?.length ? /*#__PURE__*/jsx(Header, {
      ...props
    }) : undefined,
    tooltip: tooltip
  });
}
function Header(props) {
  const startRevision = useStartRevision(props.courseDescriptor.id, props.context === "frontpage" ? () => getFirstValidChapterDescriptorInCourse(undefined, props.courseDescriptor)?.path : undefined);
  return /*#__PURE__*/jsx(IconButton, {
    className: header,
    icon: /*#__PURE__*/jsx(PlusIcon, {}),
    label: "Opret ny version",
    visibleLabel: true,
    onClick: () => startRevision(undefined)
  });
}

export { RevisionSelect };
//# sourceMappingURL=molecule.revision-select.js.map
